import { useState, useRef, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createWaitlistSignupSchema, INDUSTRY_OPTIONS } from '@new-signal/shared';
import { apiFetch } from '../lib/api';
import { track } from '../lib/analytics';
import { cn } from '../lib/utils';

const INDUSTRY_LABEL_KEYS: Record<string, string> = {
  'Technology': 'form.industryOption.technology',
  'Finance & Investment': 'form.industryOption.finance',
  'Media & Research': 'form.industryOption.media',
  'Marketing': 'form.industryOption.marketing',
  'Other': 'form.industryOption.other',
};

const inputClassName =
  'box-border w-full px-4 py-3 border border-brand-border-medium rounded-[10px] text-base leading-6 bg-brand-surface text-brand-text outline-none';

const labelClassName =
  'block mb-2 font-medium text-sm leading-5 text-brand-text tracking-[-0.01em]';

export function WaitlistForm() {
  const { t } = useTranslation('waitlist');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralSourceToken = searchParams.get('ref') || undefined;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [useCase, setUseCase] = useState('');
  const [referralSource, setReferralSource] = useState('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const formStartedRef = useRef(false);

  const handleFormInteraction = () => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      track('waitlist_form_started');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setSubmitError(null);

    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const parsed = createWaitlistSignupSchema.safeParse({
      full_name: fullName,
      email: email.trim(),
      industry: role || undefined,
      role: role.trim(),
      problem_description: useCase.trim() || undefined,
      rss_feeds_used: referralSource.trim() || undefined,
      referral_source_token: referralSourceToken,
    });

    if (!parsed.success) {
      setFieldErrors(parsed.error.flatten().fieldErrors as Record<string, string[]>);
      return;
    }

    setSubmitting(true);

    try {
      const res = await apiFetch('/waitlist', {
        method: 'POST',
        body: JSON.stringify(parsed.data),
      });

      if (res.status === 409) {
        const body = (await res.json()) as { error: string };
        track('waitlist_form_error', { error: 'duplicate_email' });
        setSubmitError(body.error);
        setSubmitting(false);
        return;
      }

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        track('waitlist_form_error', { error: 'validation_error' });
        setSubmitError((body as { error?: string })?.error ?? tc('error.generic'));
        setSubmitting(false);
        return;
      }

      const body = (await res.json()) as { referral_token: string };
      track('waitlist_form_submitted', { industry: role, role });
      navigate(`/waitlist/confirmed?token=${encodeURIComponent(body.referral_token)}`, { replace: true });
    } catch {
      setSubmitError(tc('error.network'));
      setSubmitting(false);
    }
  };

  const firstError = (field: string): string | undefined => fieldErrors[field]?.[0];

  return (
    <form
      onSubmit={handleSubmit}
      onFocus={handleFormInteraction}
      className="flex flex-col gap-6 rounded-2xl border border-brand-border bg-brand-bg p-12"
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label htmlFor="firstName" className={labelClassName}>{t('form.firstName')}</label>
          <input
            id="firstName"
            type="text"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={inputClassName}
          />
          {firstError('full_name') && (
            <p className="mt-1 text-[13px] text-brand-error">{firstError('full_name')}</p>
          )}
        </div>
        <div>
          <label htmlFor="lastName" className={labelClassName}>{t('form.lastName')}</label>
          <input
            id="lastName"
            type="text"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={inputClassName}
          />
        </div>
      </div>

      <div>
        <label htmlFor="email" className={labelClassName}>{t('form.email')}</label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClassName}
        />
        {firstError('email') && (
          <p className="mt-1 text-[13px] text-brand-error">{firstError('email')}</p>
        )}
      </div>

      <div>
        <label htmlFor="role" className={labelClassName}>{t('form.role')}</label>
        <select
          id="role"
          required
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className={cn(inputClassName, "appearance-none bg-[url('data:image/svg+xml,%3Csvg_xmlns=%27http://www.w3.org/2000/svg%27_width=%2712%27_height=%2712%27_viewBox=%270_0_24_24%27_fill=%27none%27_stroke=%27%23717182%27_stroke-width=%272%27_stroke-linecap=%27round%27_stroke-linejoin=%27round%27%3E%3Cpolyline_points=%276_9_12_15_18_9%27%3E%3C/polyline%3E%3C/svg%3E')] bg-no-repeat bg-[position:right_16px_center] pr-10")}
        >
          <option value="">{t('form.rolePlaceholder')}</option>
          {INDUSTRY_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {INDUSTRY_LABEL_KEYS[opt] ? t(INDUSTRY_LABEL_KEYS[opt]) : opt}
            </option>
          ))}
        </select>
        {firstError('role') && (
          <p className="mt-1 text-[13px] text-brand-error">{firstError('role')}</p>
        )}
      </div>

      <div>
        <label htmlFor="useCase" className={labelClassName}>{t('form.useCase')}</label>
        <textarea
          id="useCase"
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
          placeholder={t('form.useCasePlaceholder')}
          rows={3}
          className={cn(inputClassName, 'min-h-[104px] resize-y')}
        />
      </div>

      <div>
        <label htmlFor="referralSource" className={labelClassName}>{t('form.referralSource')}</label>
        <input
          id="referralSource"
          type="text"
          value={referralSource}
          onChange={(e) => setReferralSource(e.target.value)}
          className={inputClassName}
        />
      </div>

      {submitError && (
        <p className="text-sm text-brand-error">{submitError}</p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className={cn(
          'w-full min-h-[60px] rounded-full border-0 px-6 py-4 text-lg font-medium leading-7 tracking-[-0.02em] text-brand-bg',
          submitting ? 'cursor-default bg-[#444]' : 'cursor-pointer bg-accent-blue',
        )}
      >
        {submitting ? t('form.submitting') : t('form.submit')}
      </button>

      <p className="text-center text-sm leading-5 tracking-[-0.01em] text-brand-muted">
        {t('form.privacy')}
      </p>
    </form>
  );
}
